import React, { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BundleOffer, fetchBundle, getConfiguration, getCurrentLanguageCode, RootStoreState } from 'mytours-read-common'

import Modal from 'mobile_web/modules/common/components/modal'
import { Button, Card, Text } from '@mytours/components'
import BundleRedeemDialog from 'mobile_web/modules/screen/components/parts/BundleRedeemDialog'
import { mostLegibleOn } from 'common_lib/services/color'
import { formatCurrency } from '../functions'
import { Money, summariseBundleOffer, summariseBundleOffers } from '../bundleOffer'
import { useBundlePurchaseReturn } from '../useBundlePurchaseReturn'
import strings from '../../../strings'
// The chooser reuses the redeem dialog's card classes, so it depends on the same stylesheet —
// named here rather than left to arrive through the dialog import.
import 'mobile_web/modules/screen/components/styles/bundle.less'

// The half of the button pair that stands out on the page; the fill alone can be white on white.
const brandAccent = (host: HTMLElement): string | undefined => {
	const style = getComputedStyle(host)
	const probe = document.createElement('span')
	host.appendChild(probe)
	// A probe resolves `var()` and `color-mix()` to something tinycolor can read.
	const resolve = (name: string): string | undefined => {
		const value = style.getPropertyValue(name).trim()
		if (!value) {
			return undefined
		}
		probe.style.color = value
		const computed = getComputedStyle(probe).color
		const srgb = computed.match(/^color\(srgb ([\d.]+) ([\d.]+) ([\d.]+)/)
		return srgb
			? `rgb(${srgb
					.slice(1, 4)
					.map(channel => Math.round(Number(channel) * 255))
					.join(', ')})`
			: computed
	}
	const fill = resolve('--button-bg')
	const text = resolve('--button-text-color')
	const page = resolve('--background-color')
	probe.remove()

	return fill && text && page ? mostLegibleOn(page, [fill, text]) : undefined
}

/**
 * The way into a collection that a bundle sells, offered alongside whatever gate the collection has of
 * its own.
 *
 * Shown only while something actually gates the content — the collection's own code or price, or a
 * bundle force-locking it. A collection nobody has locked is not an advertising slot, so a free
 * collection that merely happens to sit in a paid bundle shows nothing.
 *
 * One bundle opens its redeem dialog directly, the same dialog a bundle link item opens from a screen.
 * Several open a chooser first, because the dialog can only speak for one bundle at a time.
 *
 * The label leads with the price and, beneath it, what the bundle holds and saves; each part is left out
 * rather than guessed when the catalogue cannot back it.
 */
const BundleUnlockButton: React.FC<{
	/** The bundles that can unlock the collection, as the payload names them — id, version and the account
	 * that owns them, which is what it takes to go and fetch each one. */
	bundles: BundleOffer[]
	accountId?: number
	className?: string
}> = ({ bundles, accountId, className = 'lock-button' }) => {
	const bundleProducts = useSelector((state: RootStoreState) => state.bundles.bundleProducts)
	const collectionWebPrices = useSelector((state: RootStoreState) => state.bundles.collectionWebPrices)
	const language = useSelector(getCurrentLanguageCode)
	const dispatch = useDispatch()
	const [chooserOpen, setChooserOpen] = useState(false)
	const [openBundleId, setOpenBundleId] = useState<number | null>(null)
	const buttonRef = useRef<HTMLElement>(null)
	const [accent, setAccent] = useState<string>()
	// A Stripe redirect lands back on whichever page the buyer started from, so a purchase begun here
	// returns here and has to be finished here rather than on the screen that owns the link group.
	// A bundle with no id is not addressable, so it can neither be shown nor fetched.
	const offered = bundles.filter((bundle): bundle is BundleOffer & { id: number } => bundle.id !== undefined)
	const bundleIds = offered.map(bundle => bundle.id)
	const { purchase, dismiss } = useBundlePurchaseReturn({
		bundleIds,
		// Each bundle names its own owner, which for a guide client's bundle is the client rather than the
		// app; the collection's account is only the fallback.
		accountIdFor: id => offered.find(bundle => bundle.id === id)?.account_id ?? accountId,
	})

	// The IAP catalogue names most bundles, but never one belonging to a guide client in another region, so
	// anything it left out is fetched by id. Keyed on the offered ids: the unknown ones shrink as they
	// arrive, and re-running on those asks again for every sibling still in flight.
	const unknown = offered.filter(bundle => !bundleProducts[bundle.id])
	const unknownRef = useRef(unknown)
	unknownRef.current = unknown
	const offeredKey = bundleIds.join(',')

	useEffect(() => {
		unknownRef.current.forEach(bundle => {
			dispatch(
				fetchBundle.started({
					devicePlatform: getConfiguration().getPlatform(),
					bundleId: bundle.id,
					bundleVersion: bundle.version ?? 0,
					accountId: bundle.account_id,
					language,
				}),
			)
		})
	}, [offeredKey, language, dispatch])

	// Ionic mounts pages off-document, so the theme only computes once the button is laid out.
	const rendered = offered.length > 0
	useLayoutEffect(() => {
		const button = buttonRef.current
		if (!rendered || !button) {
			return
		}

		const observer = new ResizeObserver(() => {
			const resolved = brandAccent(button)
			if (resolved) {
				setAccent(resolved)
				observer.disconnect()
			}
		})
		observer.observe(button)
		return () => observer.disconnect()
	}, [rendered])

	if (!rendered) {
		return null
	}

	const single = bundleIds.length === 1 ? bundleIds[0] : undefined
	const money = ({ currency, cents }: Money): string => formatCurrency(currency, cents / 100)

	let title: string
	let detail: string | undefined
	let saving: string | undefined

	if (single !== undefined) {
		const name = bundleProducts[single]?.name || strings.bundle_unlock_title
		const summary = summariseBundleOffer(single, bundleProducts, collectionWebPrices)
		title = summary.price
			? (strings.formatString(strings.bundle_offer_buy, { name, price: money(summary.price) }) as string)
			: (strings.formatString(strings.bundle_offer_get, { name }) as string)
		// A bundle of one has nothing to count, and the strings have no singular form.
		if (summary.itemCount > 1) {
			detail = strings.formatString(summary.allTours ? strings.bundle_offer_tours : strings.bundle_offer_items, {
				count: summary.itemCount,
			}) as string
		}
		if (summary.saving) {
			saving = strings.formatString(strings.bundle_offer_save, { amount: money(summary.saving) }) as string
		}
	} else {
		const { lowestPrice, biggestSaving } = summariseBundleOffers(
			bundleIds.map(bundleId => summariseBundleOffer(bundleId, bundleProducts, collectionWebPrices)),
		)
		title = lowestPrice
			? (strings.formatString(strings.bundle_offer_choose_from, { price: money(lowestPrice) }) as string)
			: strings.bundle_offer_choose
		detail = strings.formatString(strings.bundle_offer_included_in, { count: bundleIds.length }) as string
		if (biggestSaving) {
			saving = strings.formatString(strings.bundle_offer_save_up_to, { amount: money(biggestSaving) }) as string
		}
	}

	const label = [title, detail, saving].filter(Boolean).join(', ')

	const priceFor = (bundleId: number): string => {
		const webPrice = bundleProducts[bundleId]?.webPrice
		return webPrice?.amount && webPrice.currency ? formatCurrency(webPrice.currency, webPrice.amount) : ''
	}

	return (
		<>
			<Button
				ref={buttonRef}
				variant="primary"
				className={`${className} bundle-offer`}
				style={accent ? ({ '--bundle-offer-accent': accent } as CSSProperties) : undefined}
				aria-label={label}
				onClick={() => (single !== undefined ? setOpenBundleId(single) : setChooserOpen(true))}
			>
				<Text variant="inherit" className="bundle-offer-title">
					{title}
				</Text>
				{(detail || saving) && (
					<Text variant="inherit" className="bundle-offer-detail">
						{detail}
						{detail && saving && ' · '}
						{saving && <span className="bundle-offer-saving">{saving}</span>}
					</Text>
				)}
			</Button>

			{/* Mounted only while open, like every other modal here.
			    Mirrors BundleRedeemDialog's shell — same cssClass and card classes — so the chooser and the
			    dialog it leads to are the same object to a viewer, and the card, heading and full-width
			    buttons come from styles that already exist rather than a second set that drifts. */}
			{chooserOpen && (
				<Modal isOpen={true} preventNavigate cssClass="bundle-redeem-modal" onDidDismiss={() => setChooserOpen(false)}>
					<Card variant="ios" className="bundle-dialog has-badge">
						<div className="bundle-lock-badge">
							<i className="far fa-lock-alt" />
						</div>
						<h1>{strings.bundle_available_in_bundles}</h1>

						{bundleIds.map(bundleId => {
							const name = bundleProducts[bundleId]?.name || strings.bundle_unlock_title
							const price = priceFor(bundleId)

							return (
								<Button
									key={bundleId}
									variant="primary"
									className="lock-button"
									aria-label={price ? `${name} ${price}` : name}
									onClick={() => {
										setChooserOpen(false)
										setOpenBundleId(bundleId)
									}}
								>
									<Text variant="inherit">{name}</Text>
									{!!price && (
										<Text variant="inherit" className="bundle-choice-price">
											{price}
										</Text>
									)}
								</Button>
							)
						})}
					</Card>
				</Modal>
			)}

			{(openBundleId !== null || purchase) && (
				<BundleRedeemDialog
					isOpen={true}
					bundleId={purchase?.bundleId ?? (openBundleId as number)}
					accountId={purchase?.accountId ?? offered.find(bundle => bundle.id === openBundleId)?.account_id ?? accountId}
					purchaseValidating={purchase?.purchaseValidating}
					purchasedCode={purchase?.purchasedCode}
					purchasedEmail={purchase?.purchasedEmail}
					purchaseTimedOut={purchase?.purchaseTimedOut}
					onClose={() => {
						dismiss()
						setOpenBundleId(null)
					}}
				/>
			)}
		</>
	)
}

export default BundleUnlockButton
